<template lang="pug">
.author-editor.flex.flex-col.h-screen.max-h-screen.w-screen.overflow-hidden(
  ref="elRef"
)
  .author-editor-bar.flex-none.flex.gap-2.p-2.z-50.bg-gray-50.h-14.items-center.dark_bg-gray-700.shadow-lg.transition.duration-500
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Back to all courses

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/history/${firmCode}_${courseId}` : `#/history/${courseId}`"
        )
          | View #[b {{ baseYamlFilename }}] history


        template(v-if="isUbercourse")
          a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
            :href="firmCode ? `#/translate/${firmCode}_${courseId}` : `#/translate/${courseId}`"
          )
            | Translate #[b {{ baseYamlFilename }}]

          a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
            :href="firmCode ? `#/audio/${firmCode}_${courseId}` : `#/audio/${courseId}`"
          )
            | Create audio for #[b {{ baseYamlFilename }}]

          a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
            :href="firmCode ? `#/transcript/${firmCode}_${courseId}` : `#/transcript/${courseId}`"
          )
            | Export transcript for #[b {{ baseYamlFilename }}]

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/publish/${firmCode}_${courseId}` : `#/publish/${courseId}`"
          v-if="isBespoke"
        )
          | Add #[b {{ baseYamlFilename }}] to Portal

        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          :href="firmCode ? `#/metadata-editor/${firmCode}_${courseId}` : `#/metadata-editor/${courseId}`"
          v-if="!isBespoke && !isCustomisation"
        )
          | Manage metadata for #[b {{ baseYamlFilename }}]


    .px-4.text-xl.text-gray-700.dark_text-gray-400.flex.gap-4.items-center
      span
        | {{ title }}
      span(v-if="hasUnshortenedCaptionPaths")
        simple-tooltip.text-2xl.w-60(text="This course contains languages and unshortened caption paths. Please publish translations to update the paths BEFORE publishing the YAML.")
          span.text-yellow-500.text-2xl ⚠️
      span(v-if="videoCorsErrorWarnings.length > 0")
        simple-tooltip.text-2xl.w-60(:text="videoCorsErrorMessages")
          span.text-yellow-500.text-2xl ⚠️

    .ms-auto 

    .px-4.text-sm.text-gray-700.dark_text-gray-400(v-if="draft?.type === 'course' && customReport.total > 0")
      | This course has 
      a.text-blue-700.dark_text-blue-300.cursor-pointer(@click="downloadCustomisationsReport") {{ customReport.total }} customisations

    .text-sm.px-4.text-gray-700.dark_text-gray-400 {{ barSaveStatusText }}

    edit-agent-mode-picker(
      :modelValue="selectEditAgent"
      :isUbercourse="isUbercourse"
      @update:modelValue="selectEditAgent = $event"
    )
    layout-picker(
      :isUbercourse="isUbercourse"
    )
    toggle-dark

    .px-2

    simple-tooltip.text-center(
      :text="'Discard (ctrl+shift+D)' + (dirty ? ' (unsaved changes)' : '')"
      v-if="draft"
    )
      button.btn.ripple.px-4.py-2.bg-red-600.dark_bg-red-800.btn-hover_bg-red-700.dark_btn-hover_bg-red-700.dark_ripple_bg-red-500.dark_ripple_bg-opacity-20.dark_text-gray-100.disabled_saturate-0.disabled_opacity-50.text-gray-100(
        @click="discardDrawer.open"
        type="button"
        :disabled="!hasUnsavedChangesOnLoad && !hasUncommittedChanges"
      ) Discard...


    simple-tooltip.text-center(
      :text="'Save (ctrl+shift+S)' + (dirty ? ' (unsaved changes)' : '')"
      v-if="draft"
    )
      button.btn.btn-primary.ripple.px-4.py-2(
        @click="commitDrawer.open"
        type="button"
        :disabled="!hasUnsavedChangesOnLoad && !hasUncommittedChanges"
      ) Save...

    simple-tooltip.text-center(
      :text="hasUncommittedChanges ? 'Please commit before you publish.' : 'Publish (ctrl+shift+P)'"
      v-if="draft"
    )
      button.btn.btn-primary.ripple.px-4.py-2(
        :disabled="hasUncommittedChanges || hasUnsavedChangesOnLoad"
        @click="publishDialog.open"
        type="button"
      ) Publish...

    //- .px-2
    //-   user-menu

  //- add 10px because we collapse the 10px gutter
  .flex-1.flex.dark_bg-gray-800.bg-white.min-h-0
    .author-editor-pane.flex-none(
      :class="{ 'w-full': !isUbercourse }"
    )
      .prose.dark_prose-invert.mt-10.p-6.text-center.text-sm(
        v-if="loadingError"
      ) 
        p 😓 Something went wrong...
        p.italic &ldquo;{{ loadingError }}&rdquo;

        button.my-4.text-sm.btn.bg-gray-300(@click="tryAgain" type="button") Try again
      .prose.dark_prose-invert.mt-10.p-6.text-center.text-sm.animate-loading(
        v-else-if="loading"
      ) 
        p.italic Loading...
      .h-full.flex.flex-col(v-else="")
        PublishDialog.flex-none.border-b-2.border-gray-200.dark_border-gray-900(
          :draft="draft || null"
          :open="publishDialog.isOpen"
          @close="publishDialog.close"
          :customReport="customReport"
        )

        CommitDrawer.flex-none.overflow-y-auto(
          class="max-h-[70vh]"
          :draft="draft || null"
          :baseCourse="baseCourse"
          :open="commitDrawer.isOpen"
          @close="commitDrawer.close"
          @success="afterCommit"
        )

        DiscardDrawer.flex-none.overflow-y-auto(
          class="max-h-[70vh]"
          :draft="draft || null"
          :baseCourse="baseCourse"
          :open="discardDrawer.isOpen"
          @close="discardDrawer.close"
        )

        //- Editor/AI Chat Toggle moved to toolbar
        //- .author-editor-tabs.flex.bg-gray-100.flex-none.dark_bg-stone-700.shadow-inner.duration-500.h-12
        //-   span.h-full.px-4.py-2.border-b-sky-600.border-0.border-b-4.font-bold.text-base.me-auto.flex.items-center.justify-center.bg-white.bg-opacity-50.dark_bg-opacity-50.flex-0.dark_bg-gray-800.dark_text-white.border-e(
        //-     class="border-e-black/10"
        //-   )
        //-     span {{ editors[0] }}
        //- AuthorEditorPicker(
        //-   :availableFiles="availableFiles"
        //-   :modelValue="editors[0]"
        //-   @update:modelValue="setEditor(0, $event)"
        //- )

        //- Editor and AI Chat with absolute positioning and auto-alpha toggle
        .relative.flex-1.auto-alpha-fast(v-if="draft?.courseId")
          .absolute.inset-0.flex.flex-col(:class="showEditAgent ? 'auto-alpha-0' : 'auto-alpha-1'")
            AuthorMonaco(
              ref="monacoMain"
              :key="[draft?.courseId, editors[0], editorReloadNonce].join('-')"
              v-bind="filenameToEditorProps(editors[0])"
            )

          .absolute.inset-0.flex.flex-col(:class="!showEditAgent ? 'auto-alpha-0' : 'auto-alpha-1'")
            AuthorEditAgentWrapper.h-full(
              v-bind="{ courseId, firmCode, yaml: compiledYamlRef, currentScreen, isCustomisation, hasBaseCourse }"
              @view-patch="onViewPatch"
            )
    .author-editor-pane.flex-none.border-s.border-gray-500.border-opacity-20(
      v-if="layout?.includes('-2') || layout?.includes('-3')"
    )
      .h-full.flex.flex-col(v-if="draft")
        //- .author-editor-tabs.flex.bg-gray-100.flex-none.dark_bg-stone-700.shadow-inner.duration-500.h-12
        //-   span.h-full.px-4.py-2.border-b-sky-600.border-0.border-b-4.font-bold.text-base.me-auto.flex.items-center.justify-center.bg-white.bg-opacity-50.dark_bg-opacity-50.flex-0.dark_bg-gray-800.dark_text-white.border-e(
        //-     class="border-e-black/10"
        //-   )
        //-     span {{ editors[1] }}
        //- AuthorEditorPicker(
        //-   :availableFiles="availableFiles"
        //-   :modelValue="editors[1]"
        //-   @update:modelValue="setEditor(1, $event)"
        //- )
        AuthorMonaco.flex-1.w-full(
          :key="[draft?.courseId, (showEditAgent ? editors[0] : editors[1]), editorReloadNonce].join('-')"
          v-bind="filenameToEditorProps(showEditAgent ? editors[0] : editors[1])"
          v-if="draft?.courseId"
        )
    .author-editor-preview.flex-none.bg-gray-300.dark_bg-gray-800.border-s.border-gray-500.border-opacity-20(
      :class="{ hidden: !layout?.includes('pane-') }"
      v-if="isUbercourse"
    ) 
      author-preview(
        v-bind="{ courseId, firmCode }"
        v-if="!hasPopup"
        ref="previewRef"
        @current-screen="onCurrentScreen"
        @compiled-yaml="onCompiledYaml"
      )

  AuthorMessageDialog(
    :open="messageDialog.open"
    :title="messageDialog.title"
    :message="messageDialog.message"
    :actions="messageDialog.actions"
    @close="closeMessageDialog"
  )
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toValue,
  watch,
  provide
} from 'vue';
import ToggleDark from './ToggleDark.vue';
import MainMenu from './MainMenu.vue';
// import UserMenu from './UserMenu.vue';
import LayoutPicker from './LayoutPicker.vue';
import EditAgentModePicker from './EditAgentModePicker.vue';
import AuthorMonaco from './AuthorMonaco.vue';
import AuthorEditAgentWrapper from './AuthorEditAgentWrapper.vue';
import usePanes from '../lib/useEditorPanes';
import { layout, usePopup, setLayout } from '../lib/useEditorPanes';
import {
  clearCourse,
  loadCourse,
  setCourse,
  getCourse,
  courseUpdates
} from '../store/courses';
import { updateDraft } from '../lib/callModContentAuthors';
import AuthorPreview from './AuthorPreview.vue';
import AuthorMessageDialog from './AuthorMessageDialog.vue';
import useMessageDialog from '../lib/useMessageDialog';
import { useEventListener } from '@vueuse/core';
import useEditorOptions from '../store/useEditorOptions';
import extractTitle, { extractClTitle } from '../../lib/extractTitle';
import insertCompiledCss from '../lib/insertCompiledCss';
import type AuthorMonacoProps from '../../../types/AuthorMonacoProps.d.ts';
import CommitDrawer from './CommitDrawer.vue';
import PublishDialog from './PublishDialog.vue';
import DiscardDrawer from './DiscardDrawer.vue';
import SimpleTooltip from './SimpleTooltip.vue';
import { useLatestTask, DiscardedTaskError } from '../lib/useLatestTask';
import useOpener from '../lib/useOpener';
import useEditorSaveStatus from '../lib/useEditorSaveStatus';
import setValue from 'set-value';
import useDocTitle from '../lib/useDocTitle';
import getJWT from '../../lib/getJWT';
import getCourseAndFirmFromFile from '../lib/getCourseAndFirmFromFile';
import { countCustomisations, getLatestDraft } from '../../lib/callModContent';
import extractBaseCourse, {
  extractBaseClCourse
} from '../../lib/extractBaseCourse';
import getScreenById from '../lib/getScreenById';
import { useSafeLocalStorage } from '../../lib/safeLocalStorage';
import { CourseType } from '../lib/schemaRegistry';
import { checkVideoCors } from '../lib/callModContentAuthors';
import timestamp from '../../../esm/lib/time-stamp.js';
import jsYaml from 'js-yaml';
import languagesMap from '../../lib/languages.json';
import activeLanguages from '../../lib/activeLanguages.yml';

const props = defineProps<{ courseId: number; firmCode: string }>();
const elRef = ref<HTMLElement>();
// const courseWindow = ref<CourseWindow>();
const loading = ref(true);
const loadingError = ref('');
const draft = ref<CourseDraft>();
const { toggleWordWrap } = useEditorOptions();
const previewRef = ref<InstanceType<typeof AuthorPreview>>();
const isCustomisation = ref(false);
const isBespoke = ref(false);
const selectEditAgent = useSafeLocalStorage('selectEditAgent', false);
const isUbercourse = computed(
  () => (draft.value?.activityName || 'Full Course') === 'Full Course'
);
// True when the course's content comes from a base-course (e.g. duplicates):
// the editor model holds only patches, so Base apply mode would fail.
const hasBaseCourse = computed(() => {
  const y = draft.value?.yaml;
  if (!y) return false;
  return !!(isUbercourse.value ? extractBaseCourse(y) : extractBaseClCourse(y));
});
const showEditAgent = computed(
  () => selectEditAgent.value && isUbercourse.value
);

const commitDrawer = useOpener();
const publishDialog = useOpener();
const discardDrawer = useOpener();

const {
  originalYamlRef,
  lastSavedYamlRef,
  lastCommittedYamlRef,
  lastTouchedYamlRef,
  originalCssRef,
  lastSavedCssRef,
  lastCommittedCssRef,
  lastTouchedCssRef,
  dirty,
  error,
  hasUncommittedChanges,
  newCommitSaved,
  draftSaved,
  onCommit
} = useEditorSaveStatus();

const translationSavedFlash = ref(false);
let translationSavedFlashTimer: ReturnType<typeof setTimeout> | null = null;

const flashTranslationSaved = () => {
  if (translationSavedFlashTimer) {
    clearTimeout(translationSavedFlashTimer);
    translationSavedFlashTimer = null;
  }
  translationSavedFlash.value = true;
  translationSavedFlashTimer = setTimeout(() => {
    translationSavedFlash.value = false;
    translationSavedFlashTimer = null;
  }, 3000);
};

const barSaveStatusText = computed(() => {
  if (error.value) return error.value;
  if (translationSavedFlash.value) return 'Translation saved.';
  if (newCommitSaved.value) return 'Changes saved';
  if (draftSaved.value) return 'Unsaved changes';
  if (dirty.value) return 'Unsaved changes';
  return '';
});

const afterCommit = async (validation?: SaveValidationResult) => {
  // The save-time validation phase may rewrite the YAML server-side (e.g.
  // adding missing IDs) as a secondary commit. When it does, the editor's
  // in-memory YAML is now stale; reseed it and remount Monaco so the editor +
  // preview match what was stored. Monaco isn't reactive to props and is
  // read-only while the commit drawer is open (so executeEdits is a no-op),
  // hence a remount rather than an in-place edit.
  if (validation?.changed && validation.yaml && draft.value) {
    const newYaml = validation.yaml;

    // Drop any pending/in-flight autosave so it can't recreate an ID-less
    // draft after the commit deleted it.
    queueUpdate.cancel?.();

    draft.value = { ...draft.value, yaml: newYaml };
    setCourse(draft.value);

    // Leave originalYamlRef so the "Changes saved" status still shows; keep
    // saved/touched/committed in sync so the editor isn't marked dirty.
    lastSavedYamlRef.value = newYaml;
    lastTouchedYamlRef.value = newYaml;
    lastCommittedYamlRef.value = newYaml;
    lastCommittedCssRef.value = lastTouchedCssRef.value;

    // The committed draft was deleted server-side; let the next autosave adopt
    // a fresh draft identity rather than chasing the deleted one.
    alreadyUpdatedUuid.value = false;

    // Force Monaco to remount and re-read the reseeded YAML.
    editorReloadNonce.value++;

    updatePreview();
    hasUnsavedChangesOnLoad.value = false;
    return;
  }

  lastCommittedYamlRef.value = lastTouchedYamlRef.value;
  lastCommittedCssRef.value = lastTouchedCssRef.value;
  hasUnsavedChangesOnLoad.value = false;
};

// ---------------- Patch accept forwarding ----------------
// Reference to the primary editor (first pane)
const monacoMain = ref<InstanceType<typeof AuthorMonaco>>();

// Bumped to force the Monaco editor(s) to remount (re-reading draft.value.yaml)
// after the validation phase rewrites the saved YAML.
const editorReloadNonce = ref(0);

const updatePreview = () => setTimeout(() => previewRef.value?.update?.(), 100);

// Set by the agent while it streams OR while edits await review. The preview
// locks inline editing so user edits can't go stale against pending changes.
const inlineEditLocked = ref(false);
provide('inlineEditLocked', inlineEditLocked);

// Reason the inline editor is locked, surfaced in the "editing paused" dialog.
const inlineEditLockReason = ref<'streaming' | 'review' | null>(null);
provide('inlineEditLockReason', inlineEditLockReason);

// One shared dialog so pending-review and editing-paused can never co-exist.
const { messageDialog, openMessageDialog, closeMessageDialog } =
  useMessageDialog();
provide('messageDialog', messageDialog);
provide('openMessageDialog', openMessageDialog);
provide('closeMessageDialog', closeMessageDialog);

provide('acceptPatch', (screenYaml: string) => {
  monacoMain.value?.acceptPatch?.(screenYaml);
  updatePreview();
});

// Raw handlers for AuthorEditAgentWrapper routing
// After modifying Monaco, trigger preview update
provide('acceptBaseRaw', (screenYaml: string) => {
  monacoMain.value?.acceptBase?.(screenYaml);
  updatePreview();
});

provide('acceptPatchRaw', (screenYaml: string) => {
  monacoMain.value?.acceptPatch?.(screenYaml);
  updatePreview();
});

provide('revertPatchRaw', (screenId: string) => {
  monacoMain.value?.revertPatch?.(screenId);
  updatePreview();
});

// Preview handlers for PatchPreview component
provide('previewPatch', (screenYaml: string) => {
  previewRef.value?.previewPatch?.(screenYaml);
});

provide('clearPreviewPatch', (screenId: string, keepDiff?: boolean) => {
  previewRef.value?.clearPreviewPatch?.(screenId, keepDiff);
});

// monaco.editor.getModels().forEach(model => model.dispose());

// @ts-ignore
(window as EditorWindow).getEditorDraft = () => getCourse(props)?.draft;

// @ts-ignore
window.syncCourseToRef = (draftRef: Ref<CourseDraft | undefined>) => {
  // Seed the current draft: loadCourse emits before AuthorPreview mounts (it's
  // gated behind `loading`), so the listener below would miss the initial load.
  const current = getCourse(props)?.draft;
  if (current) draftRef.value = { ...current };

  // Returns the unsubscribe handle so AuthorPreview can detach on unmount;
  // otherwise each remount stacks another courseUpdates listener.
  return courseUpdates.on(d => {
    const sameCourseId = d.courseId === props.courseId;
    const sameFirmCode = !props.firmCode || d.firmCode === props.firmCode;

    if (sameCourseId && sameFirmCode) {
      draftRef.value = { ...d };
    }
  });
};

// Expose acceptFromPreview for TipTap inline edits
// Checks apply mode to determine whether to use acceptBase or acceptPatch
// @ts-ignore
(window as EditorWindow).acceptFromPreview = (screenYaml: string) => {
  const applyMode = (window as EditorWindow).getApplyMode?.();
  if (applyMode === 'base') {
    monacoMain.value?.acceptBase?.(screenYaml);
  } else {
    // Default to patches mode
    monacoMain.value?.acceptPatch?.(screenYaml);
  }
  updatePreview();
};

// Expose acceptPathFromPreview for shorthand inline edits like $course.title
// @ts-ignore
(window as EditorWindow).acceptPathFromPreview = (
  path: string,
  value: string
) => {
  void acceptPathFromPreview(path, value);
};

// Expose acceptHiddenFromPreview for include/exclude toggles in the preview.
// `hidden` is a top-level map, not a patch, so it goes through acceptHidden.
// @ts-ignore
(window as EditorWindow).acceptHiddenFromPreview = (
  id: string,
  hidden: boolean
) => {
  monacoMain.value?.acceptHidden?.(id, hidden);
  updatePreview();
};

// Expose acceptPatchesFromPreview for structural edits in the preview (e.g.
// adding a page). `patches` is a top-level map; the value is a JSON string.
// @ts-ignore
(window as EditorWindow).acceptPatchesFromPreview = (patchesJson: string) => {
  monacoMain.value?.acceptPatches?.(patchesJson);
  updatePreview();
};

// Expose acceptActivePreview to clear preview after accepting inline edit
// @ts-ignore
(window as EditorWindow).acceptActivePreview = (screenId: string) => {
  previewRef.value?.clearPreviewPatch?.(screenId);
};

// @ts-ignore
(window as EditorWindow).notifyTranslationSavedFromPreview =
  flashTranslationSaved;

const baseCourse = ref<CourseDraft>();
const customReport = ref({});

const hasUnsavedChangesOnLoad = ref(false);

interface VideoCorsWarning {
  videoId: string;
  url: string;
  message: string;
  corsHeader: string | null;
}

const videoCorsErrorWarnings = ref<VideoCorsWarning[]>([]);

const load = async () => {
  try {
    const r = await loadCourse(props);
    if (!r) throw new Error('Course not found.');
    let { latest: d, latestDraft, hasUnsavedChanges } = r;
    if (!d?.courseId) throw new Error('Course not found.');

    isCustomisation.value = d.type === 'custom';
    isBespoke.value = d.type === 'bespoke';

    if (hasUnsavedChanges) {
      const draftAuthor = latestDraft?.author;

      hasUnsavedChangesOnLoad.value = true;

      if (draftAuthor !== getJWT().userProfile.email) {
        const confirm = window.confirm(
          `This course has unsaved changes by ${draftAuthor}. If you proceed, you risk overwriting each other's work.\n\nDo you want to proceed?`
        );

        if (!confirm) {
          throw new Error(`Course has unsaved changes from ${draftAuthor}.`);
        }
      }
    }

    d = await insertCompiledCss(d, { memoize: 'session' });
    draft.value = d;
    loading.value = false;
    // FIX: lastSavedYamlRef is updating as a draft because d is latest, not necessarily the saved course
    originalYamlRef.value = d.yaml;
    lastSavedYamlRef.value = d.yaml;
    lastTouchedYamlRef.value = d.yaml;
    lastCommittedYamlRef.value = d.yaml;
    originalCssRef.value = d.css;
    lastSavedCssRef.value = d.css;
    lastTouchedCssRef.value = d.css;
    lastCommittedCssRef.value = d.css;

    if (props.firmCode) {
      const baseCourseId = isUbercourse.value
        ? extractBaseCourse(d.yaml)
        : extractBaseClCourse(d.yaml);
      const r = await loadCourse({
        courseId: baseCourseId ?? d.courseId
      });
      if (r) baseCourse.value = r.latestSaved;
    }

    if (d.type === 'course' && isUbercourse.value) {
      const report = await countCustomisations(props.courseId);

      customReport.value = report;
    }

    if (!isUbercourse.value) {
      setLayout('pane-0');
    } else {
      setLayout('pane-1');
    }
  } catch (e) {
    loadingError.value = e.message;
  }
};

onMounted(load);

onBeforeUnmount(() => {
  delete (window as unknown as EditorWindow).notifyTranslationSavedFromPreview;
  if (translationSavedFlashTimer) {
    clearTimeout(translationSavedFlashTimer);
    translationSavedFlashTimer = null;
  }
});

// Configuration object for customisations report
const reportConfig = {
  includeAudioOnly: true,
  includeCustomLogo: true,
  includeCustomTitle: true,
  includeCustomContent: true,
  includePatchedScreens: true,
  includeLanguages: true,
  includeAudioLanguages: true,
  includeLastUpdated: true,
  includeDraftStatus: false
};

// Function to analyze course YAML and extract relevant information
const analyzeCourseYaml = (course: any) => {
  try {
    const parsed = jsYaml.load(course.yaml) as any;
    const analysis: any = {
      courseId: course.courseId,
      firmCode: course.firmCode,
      title: course.title,
      baseCourseId: parsed['base-course'] ? parsed['base-course'] : '',
      lastUpdated: course.updatedDate,
      hasAudio: course.hasAudio
    };

    // Languages
    if (reportConfig.includeLanguages) {
      const languages: string[] = [];
      if (parsed?.languages) {
        parsed?.languages?.forEach((lang: string) => {
          if (languagesMap[lang]) {
            languages.push(lang);
          }
        });
      }

      // do we ever uses patches for the languages?
      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          if (patchKey.startsWith('$course.languages.')) {
            const lang = patchKey.replace('$course.languages.', '');
            if (languagesMap[lang] && !languages.includes(lang)) {
              languages.push(lang);
            }
          }
        });
      }
      analysis.languages = languages.join(', ');
      analysis.languageCount = languages.length;
    }

    // Audio languages
    if (reportConfig.includeAudioLanguages) {
      const audioLanguages: string[] = [];
      if (parsed?.course?.audio) {
        Object.keys(parsed.course.audio).forEach(lang => {
          if (languagesMap[lang]) {
            audioLanguages.push(lang);
          }
        });
      }
      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          if (patchKey.startsWith('$course.audio.')) {
            const lang = patchKey.replace('$course.audio.', '');
            if (languagesMap[lang] && !audioLanguages.includes(lang)) {
              audioLanguages.push(lang);
            }
          }
        });
      }
      analysis.audioLanguages = audioLanguages.join(', ');
    }

    // Custom title
    if (reportConfig.includeCustomTitle) {
      analysis.hasCustomTitle = !!(
        parsed.course?.title ||
        (parsed.patches &&
          Object.keys(parsed.patches).some(key => key === '$course.title'))
      );
    }

    // Custom logo
    if (reportConfig.includeCustomLogo) {
      analysis.hasCustomLogo = !!(
        parsed.course?.logo ||
        (parsed.patches &&
          Object.keys(parsed.patches).some(key => key === '$course.logo'))
      );
    }

    // Full customisation (has pages)
    if (reportConfig.includeCustomContent) {
      analysis.hasFullCustomisation = !!(
        parsed.pages &&
        Array.isArray(parsed.pages) &&
        parsed.pages.length > 0
      );
    }

    // Patched screens analysis
    if (reportConfig.includePatchedScreens) {
      const patchedScreens: string[] = [];
      const shorthandPatches: string[] = [];

      if (parsed.patches) {
        Object.keys(parsed.patches).forEach(patchKey => {
          // Check if it's a shorthand patch (starts with $ or #)
          if (patchKey.startsWith('$') || patchKey.startsWith('#')) {
            shorthandPatches.push(patchKey);
          } else {
            // Check if it's a screen patch by looking for the patch structure
            const patchValue = parsed.patches[patchKey];
            if (
              Array.isArray(patchValue) &&
              patchValue.length > 0 &&
              patchValue[0] &&
              typeof patchValue[0] === 'object' &&
              patchValue[0].id
            ) {
              patchedScreens.push(patchKey);
            }
          }
        });
      }

      analysis.patchedScreens = patchedScreens.join(', ');
      analysis.shorthandPatches = shorthandPatches.join(', ');
      analysis.patchedScreensCount = patchedScreens.length;
      analysis.shorthandPatchesCount = shorthandPatches.length;
    }

    // Audio-only detection
    if (reportConfig.includeAudioOnly) {
      const hasAudio = analysis.hasAudio || false;
      const hasFullCustomisation = analysis.hasFullCustomisation || false;
      const hasPatchedScreens = (analysis.patchedScreensCount || 0) > 0;
      const hasCustomLogo = analysis.hasCustomLogo || false;
      const hasCustomTitle = analysis.hasCustomTitle || false;

      // Audio-only if it has audio but no full customisation, no patched screens, and minimal other customisations
      analysis.isAudioOnly =
        hasAudio &&
        !hasFullCustomisation &&
        !hasPatchedScreens &&
        ((!hasCustomLogo && !hasCustomTitle) ||
          hasCustomLogo ||
          hasCustomTitle);
    }

    return analysis;
  } catch (error) {
    console.error('Error analyzing course YAML:', error);
    return {
      courseId: course.courseId,
      firmCode: course.firmCode,
      title: course.title,
      error: 'Failed to parse YAML'
    };
  }
};

// Function to convert analysis data to TSV and trigger download
const downloadCustomisationsReport = () => {
  if (customReport.value.total === 0) return;

  const dts = timestamp('YYYY-MM-DD-HHmmss');
  // const allCourses: any[] = [];

  // Collect all customisations
  /*customReport.value.list.forEach((course: any) => {
    allCourses.push({
      ...course,
      baseCourseId: course.baseCourseId
    });
  });*/

  // Analyze each course
  const analyzedCourses = customReport.value.list.map(course =>
    analyzeCourseYaml(course)
  );

  // Define TSV headers based on configuration
  const headers = ['Firm Code', 'Course ID', 'Base Course ID', 'Title'];

  if (reportConfig.includeLanguages) {
    headers.push('Languages', 'Language Count');
  }
  if (reportConfig.includeAudioLanguages) {
    headers.push('Audio Languages', 'Has Audio');
  }
  if (reportConfig.includeCustomTitle) {
    headers.push('Has Custom Title');
  }
  if (reportConfig.includeCustomLogo) {
    headers.push('Has Custom Logo');
  }
  if (reportConfig.includeCustomContent) {
    headers.push('Has Full Customisation');
  }
  if (reportConfig.includePatchedScreens) {
    headers.push(
      'Patched Screens',
      'Patched Screens Count',
      'Shorthand Patches',
      'Shorthand Patches Count'
    );
  }
  if (reportConfig.includeAudioOnly) {
    headers.push('Is Audio Only');
  }
  if (reportConfig.includeLastUpdated) {
    headers.push('Last Updated');
  }
  if (reportConfig.includeDraftStatus) {
    headers.push('Has Unsaved Changes', 'Draft Author', 'Draft Date');
  }

  // Create TSV rows
  const tsvRows = [headers.join('\t')];

  analyzedCourses.forEach(course => {
    const row = [
      course.firmCode,
      course.courseId,
      course.baseCourseId,
      course.title
    ];

    if (reportConfig.includeLanguages) {
      row.push(course.languages || '', course.languageCount || 0);
    }
    // if we have detected an audio prop in the yaml but no audio languages, then it's an old format with audio somewhere in the yaml (not course level)
    if (reportConfig.includeAudioLanguages) {
      row.push(
        course.audioLanguages || '',
        course.hasAudio && course.audioLanguages
          ? 'Yes'
          : course.hasAudio
            ? 'Yes (old format?)'
            : 'No'
      );
    }
    if (reportConfig.includeCustomTitle) {
      row.push(course.hasCustomTitle ? 'Yes' : 'No');
    }
    if (reportConfig.includeCustomLogo) {
      row.push(course.hasCustomLogo ? 'Yes' : 'No');
    }
    if (reportConfig.includeCustomContent) {
      row.push(course.hasFullCustomisation ? 'Yes' : 'No');
    }
    if (reportConfig.includePatchedScreens) {
      row.push(
        course.patchedScreens || '',
        course.patchedScreensCount || 0,
        course.shorthandPatches || '',
        course.shorthandPatchesCount || 0
      );
    }
    if (reportConfig.includeAudioOnly) {
      row.push(course.isAudioOnly ? 'Yes' : 'No');
    }
    if (reportConfig.includeLastUpdated) {
      row.push(
        course.lastUpdated
          ? new Date(Number(course.lastUpdated)).toLocaleString()
          : ''
      );
    }
    if (reportConfig.includeDraftStatus) {
      row.push(
        course.hasUnsavedChanges ? 'Yes' : 'No',
        course.draftAuthor || '',
        course.draftDate || ''
      );
    }

    tsvRows.push(row.join('\t'));
  });

  // Create and download the file
  const tsvContent = tsvRows.join('\n');
  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `customisations-report-${props.courseId}-${dts}.tsv`;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  a.remove();
};

const tryAgain = () => {
  clearCourse(props);
  load();
};

const videoCorsErrorMessages = computed(() => {
  if (
    !videoCorsErrorWarnings.value ||
    videoCorsErrorWarnings.value.length === 0
  ) {
    return '';
  }

  const messages = videoCorsErrorWarnings.value.map(warning => {
    return warning.videoId;
  });

  messages.unshift(
    'This course contains videos with cors errors. Please re-upload the videos and check the URL BEFORE publishing the YAML:'
  );

  return messages.join(' ');
});

const availableFiles = computed(() => {
  if (props.firmCode)
    return [
      `${props.firmCode}_${props.courseId}.yml`,
      `${props.firmCode}_${props.courseId}.css`,
      `${props.courseId}.yml`,
      `${props.courseId}.css`
    ];

  return [`${props.courseId}.yml`, `${props.courseId}.css`];
});

const editors = computed<[string, string]>(() => {
  const saved: [string, string] = getCourse(props)?.preview?.editors || [
    '',
    ''
  ];

  if (saved[0] && saved[1]) return saved;

  const unused = availableFiles.value.filter(f => !saved.includes(f));

  if (!saved[0]) {
    saved[0] = unused[0];
    unused.splice(0, 1);
  }

  if (!saved[1]) {
    saved[1] = unused[0];
  }

  return saved;
});

const setEditor = (i: number, file: string) => {
  setValue(getCourse(props), `preview.editors.${i}`, file);
};

const filenameToEditorProps = (filename: string): AuthorMonacoProps => {
  const [name, ext] = filename.split('.');
  const { firmCode, courseId } = getCourseAndFirmFromFile(name);

  const value =
    ext === 'yml'
      ? draft.value?.yaml
      : draft.value?.css?.split('\n/* TAILWIND */\n')[0];

  const lang = ext === 'yml' ? 'yaml' : 'scss';
  const onChange = changeHandlers[ext];
  const activityType =
    draft.value?.activityName === 'Conversational Learning'
      ? CourseType.ConversationalLearning
      : CourseType.FullCourse;

  const readOnly = props.firmCode ? props.firmCode !== firmCode : false;
  const isDrawerOpen = commitDrawer.isOpen.value || discardDrawer.isOpen.value;

  return {
    courseId,
    firmCode,
    lang,
    value,
    filename,
    onChange,
    activityType,
    readOnly: readOnly || isDrawerOpen
  };
};

const baseYamlFilename = computed(() =>
  props.firmCode
    ? `${props.firmCode}_${props.courseId}.yml`
    : `${props.courseId}.yml`
);

const title = computed(() => draft.value?.title || '');
useDocTitle(title);

const hasUnshortenedCaptionPaths = computed(() => {
  if (!draft.value?.yaml) return false;
  if (!draft.value.yaml.includes('languages:')) return false;

  // extract all of the captions props to an array
  const captionsProps = draft.value.yaml.match(/^(\s*)captions:.*?(\n|$)/gm);
  if (!captionsProps) return false;

  // separate the captions props into an array of objects
  const captionsPropsArray = captionsProps.map(prop => {
    const [key, value] = prop.split(':');
    return { key: key.trim(), value: value.trim() };
  });

  // check if any of the captions props do not start with captions/ or custom/
  return captionsPropsArray.some(
    prop =>
      !prop.value.startsWith('captions/') && !prop.value.startsWith('custom/')
  );
});

const alreadyUpdatedUuid = ref(false);

const updateDraftAndUuid = async (d: CourseDraft) => {
  await updateDraft(d, baseCourse.value);
  if (alreadyUpdatedUuid.value === false) {
    // Adopt the server-assigned draft identity only. Never replace yaml/css:
    // the refetch can return stale data (read-after-write lag, or the saved
    // version winning the latest-comparison) and would silently drop edits.
    const r = await getLatestDraft({
      courseId: d.courseId,
      firmCode: d.firmCode
    });
    if (r?.id && draft.value) {
      draft.value = { ...draft.value, id: r.id, metadata: r.metadata };
    }
  }
  alreadyUpdatedUuid.value = true;
};

const queueUpdate = useLatestTask(updateDraftAndUuid);

let i = 0;

const save = async (d: CourseDraft) => {
  if (!d) return;
  const title = isUbercourse.value
    ? extractTitle(d.yaml, baseCourse.value?.yaml)
    : extractClTitle(d.yaml, baseCourse.value?.yaml);
  if (title) d.title = title;
  d = await insertCompiledCss(d, { memoize: 'session' });
  draft.value = d;
  setCourse(d);
  lastTouchedYamlRef.value = d.yaml;
  lastTouchedCssRef.value = d.css;
  const j = i++;

  try {
    // console.log('queue - Adding: ', j, [d.yaml.slice(0, 80)]);
    await queueUpdate(d, baseCourse.value);
    // console.log('queue - Success: ', j, [d.yaml.slice(0, 80)]);

    const corsError = await checkVideoCors(props.courseId, props.firmCode);
    if (corsError.response.status === 200 && corsError.data?.hasErrors) {
      error.value = `🚨 Error: ${corsError.data.message}`;
      videoCorsErrorWarnings.value = corsError.data.warnings;
    } else {
      error.value = '';
      videoCorsErrorWarnings.value = [];
    }
  } catch (e) {
    if (e instanceof DiscardedTaskError) {
      // console.log('queue - Discarded: ', j, [d.yaml.slice(0, 80)]);
    } else {
      error.value = '🚨 Error: ' + (e.message || e.status || '');
    }
  }

  lastSavedYamlRef.value = d.yaml;
  lastSavedCssRef.value = d.css;
};

const acceptPathFromPreview = async (path: string, value: string) => {
  if (!path) return;

  try {
    monacoMain.value?.acceptPath?.(path, value);
    updatePreview();
  } catch (e) {
    console.error('Failed to accept shorthand inline edit:', e);
  }
};

const changeHandlers = {
  yml: async (yaml: string) => {
    if (!draft.value) return;
    let curr: CourseDraft = { ...draft.value, yaml };
    save(curr);
  },

  css: async (css: string) => {
    if (!draft.value) return;
    let curr: CourseDraft = { ...draft.value, css };
    save(curr);
  }
};

const onKeydown = (e: KeyboardEvent) => {
  const preview = previewRef.value;

  const handlers: [string, () => void][] =
    (e.ctrlKey || e.metaKey) && e.shiftKey
      ? [
          ['p', () => publishDialog.open()],
          ['s', () => commitDrawer.open()],
          ['d', () => discardDrawer.open()],
          ['r', () => preview?.reset()]
        ]
      : e.ctrlKey || e.metaKey
        ? [
            ['s', () => preview?.update()],
            ['r', () => preview?.refresh()]
          ]
        : e.altKey
          ? [['z', toggleWordWrap]]
          : e.shiftKey
            ? [
                // ['F1', quickCommand],
                // ['F2', format],
                // ['F3', find],
                ['F5', () => preview?.reset()]
              ]
            : [
                // ['F1', quickCommand],
                // ['F2', format],
                // ['F3', find],
                ['F5', () => preview?.refresh()]
              ];

  for (const [key, callback] of handlers) {
    if (e.key !== key) continue;
    e.preventDefault();
    callback();
    return;
  }
};

const courseWindow = computed(() => toValue(previewRef.value?.courseWindow));
useEventListener('keydown', onKeydown);
useEventListener(courseWindow, 'keydown', onKeydown);

const { hasPopup } = usePanes(elRef);
usePopup(props, draft);

const yaml = computed(() => draft.value?.yaml ?? '');

// Compiled YAML provided by AuthorPreview
const compiledYamlRef = ref<string | undefined>(undefined);

function onCompiledYaml(yamlStr: string) {
  compiledYamlRef.value = yamlStr;
}

const currentScreen = ref<UcScreen | null>(null);

function onCurrentScreen(screen: any) {
  console.log('AuthorEditor received current-screen', screen);
  currentScreen.value = screen;
}

watch(currentScreen, v => {
  console.log('AuthorEditor currentScreen changed', v);
});

function onViewPatch(pageId: string, screenId: string) {
  console.log('AuthorEditor received view-patch', { pageId, screenId });

  // Use the goToSection method from the preview component
  if (previewRef.value?.goToSection) {
    previewRef.value.goToSection(pageId, screenId);
  } else {
    console.warn('goToSection method not available on preview');
  }
}
</script>
